import LangEnum from '../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../translation/TranslationContext';
import {
  getDesiredStatus,
  getStatusChangeDirection,
} from '../../../../actions/utils/orderListMassActions.utils';
import {
  BatchChangeOrderStatusParams,
  BatchUpdateOrdersParams,
} from '../../../../interfaces/orderBatchRepository.interfaces';
import ordersBatchRepository from '../../../../repositories/ordersBatch.repository';
import { HandleChangeStatusParams } from '../../components/types/OrderListMassActionsAlertContent.types';
import { MassActionResult } from '../../types/orderListMassActions.types';

interface UseHandleOrderListMassActionsProps {
  batchUpdateOrdersParams: BatchUpdateOrdersParams;
}

const useHandleOrderListMassActions = ({
  batchUpdateOrdersParams,
}: UseHandleOrderListMassActionsProps) => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const repository = ordersBatchRepository();

  const showMassActionsSuccessFeedback = (feedbackText: LangEnum) =>
    handleMessage({
      type: 'info',
      message: translate(feedbackText),
    });

  const showMassActionsErrorFeedback = (feedbackText: LangEnum) =>
    handleMessage({
      type: 'error',
      message: translate(feedbackText),
    });

  const handleChangeStatus = async ({
    action,
    updateConditionsAndPrices,
    isUpdateConditionsOnStatusChangeEnabled,
    customActionType,
  }: HandleChangeStatusParams): Promise<MassActionResult> => {
    try {
      const desiredStatus = getDesiredStatus(action);
      const statusChangeDirection = getStatusChangeDirection(action);
      const canUpdateConditionsAndPrices =
        isUpdateConditionsOnStatusChangeEnabled && updateConditionsAndPrices;

      const payload: BatchChangeOrderStatusParams = {
        desiredStatus,
        statusChangeDirection,
        updateConditionsAndPrices: canUpdateConditionsAndPrices,
        ...batchUpdateOrdersParams,
      };

      const response = customActionType
        ? await repository.batchChangeOrderStatusByAction(customActionType, payload)
        : await repository.batchChangeOrderStatus(payload);

      if (response.ok) {
        showMassActionsSuccessFeedback(
          handleMassActionFeedbackToShow(canUpdateConditionsAndPrices),
        );
        return response;
      }

      showMassActionsErrorFeedback(LangEnum.ORDER_MASS_ACTIONS_STATUS_CHANGE_ERROR_FEEDBACK);
      return response;
    } catch (_error) {
      showMassActionsErrorFeedback(LangEnum.ORDER_MASS_ACTIONS_STATUS_CHANGE_ERROR_FEEDBACK);
      return { ok: false };
    }
  };

  const handleMassActionFeedbackToShow = (sentToUpdateConditionsAndPrices: boolean) =>
    sentToUpdateConditionsAndPrices
      ? LangEnum.ORDER_MASS_ACTIONS_UPDATE_STATUS_CHANGE_FEEDBACK
      : LangEnum.ORDER_MASS_ACTIONS_STATUS_CHANGE_FEEDBACK;

  const handleArchiveOrders = async (archived: boolean): Promise<MassActionResult> => {
    const response = await repository.batchArchiveOrders({ archived, ...batchUpdateOrdersParams });

    if (response.ok) {
      showMassActionsSuccessFeedback(
        archived
          ? LangEnum.ORDER_MASS_ACTIONS_ARCHIVE_ORDERS_FEEDBACK
          : LangEnum.ORDER_MASS_ACTIONS_UNARCHIVE_ORDERS_FEEDBACK,
      );
      return response;
    }
    showMassActionsErrorFeedback(
      archived
        ? LangEnum.ORDER_MASS_ACTIONS_ARCHIVE_ORDERS_ERROR_FEEDBACK
        : LangEnum.ORDER_MASS_ACTIONS_UNARCHIVE_ORDERS_ERROR_FEEDBACK,
    );
    return response;
  };

  const handleUpdatePrices = async (): Promise<MassActionResult> => {
    const response = await repository.batchUpdatePrices(batchUpdateOrdersParams);
    if (response.ok) {
      showMassActionsSuccessFeedback(LangEnum.ORDER_MASS_ACTIONS_UPDATE_PRICES_FEEDBACK);
      return response;
    }
    showMassActionsErrorFeedback(LangEnum.ORDER_MASS_ACTIONS_UPDATE_PRICES_ERROR_FEEDBACK);
    return response;
  };

  const handleUpdateConditions = async (): Promise<MassActionResult> => {
    const response = await repository.batchUpdateConditions(batchUpdateOrdersParams);
    if (response.ok) {
      showMassActionsSuccessFeedback(LangEnum.ORDER_MASS_ACTIONS_UPDATE_CONDITIONS_FEEDBACK);
      return response;
    }
    showMassActionsErrorFeedback(LangEnum.ORDER_MASS_ACTIONS_UPDATE_CONDITIONS_ERROR_FEEDBACK);
    return response;
  };

  const handleSplitOrders = async (): Promise<MassActionResult> => {
    const response = await repository.batchSplitOrders(batchUpdateOrdersParams);
    if (response.ok) {
      showMassActionsSuccessFeedback(LangEnum.ORDER_MASS_ACTIONS_SPLIT_ORDERS_FEEDBACK);
      return response;
    }
    showMassActionsErrorFeedback(LangEnum.ORDER_MASS_ACTIONS_SPLIT_ORDERS_ERROR_FEEDBACK);
    return response;
  };

  return {
    handleChangeStatus,
    handleArchiveOrders,
    handleUpdatePrices,
    handleUpdateConditions,
    handleSplitOrders,
  };
};

export default useHandleOrderListMassActions;
